/* ============================================================================
   RDG — MOTEUR D'ANIMATION
   Refonte DA 2026-08 · lot 4 · cree le 20/08/2026

   Trois primitives : reveal-up, mask-line, sheet-in (cette derniere arrive au
   lot 6, en meme temps que la refonte du panier).

   ---------------------------------------------------------------------------
   CONTRATS ABSOLUS — chacun vient d'une lecture du theme, pas d'une precaution
   ---------------------------------------------------------------------------
   · Ne JAMAIS utiliser les attributs reveal / can-reveal / reveal-on-scroll :
     c'est la surface d'API du moteur natif, encore instancie sans condition
     par custom.js:171. On n'emploie que des classes rdg-*.
   · Ne JAMAIS nommer une fonction « reveal » : c'est une methode publique du
     theme (global.js:3549, appelee par facets.js:279 et :566).
   · Ne JAMAIS envelopper ni desarmer inView globalement : il sert aussi a des
     chargements FONCTIONNELS (variantes produit global.js:2719, pagination
     des facettes facets.js:210).
   · Ne JAMAIS lire .time / .speed / .state sur le retour d'animate() : sous
     skipAnimations le groupe est vide et l'acces leve une TypeError.
   · L'import DOIT passer par le specificateur nu '@theme/m'. Une URL d'asset
     creerait une SECONDE instance du module, donc un second
     MotionGlobalConfig que personne ne lit : l'interrupteur paraitrait pose
     et serait muet. Verifie au navigateur le 20/08 :
     (await import('@theme/m')).MotionGlobalConfig ===
     (await import('@theme/m-utils')).MotionGlobalConfig  -> true
   · Ne JAMAIS poser transform ni will-change sur un ancetre commun aux deux
     colonnes de la fiche produit : elles sont en position:sticky au-dessus de
     1024px (base.css:15261) et un ancetre transforme annule le sticky.
   · Ne JAMAIS observer un element translate a l'interieur d'un overflow:hidden
     (carrousels) : le rectangle d'intersection est vide en permanence, le
     callback ne part jamais, le contenu reste invisible A VIE. On observe le
     CONTENEUR et on revele les enfants en cascade.
   · Ne JAMAIS se fier a intersectionRatio : sur un conteneur plus haut que le
     viewport il plafonne et n'atteint jamais 0.15. On lit isIntersecting.
   ============================================================================ */

import { MotionGlobalConfig, animate } from '@theme/m';

const REDUIT = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ----------------------------------------------------------------------------
   COUCHE 3 DE L'ACCESSIBILITE — l'interrupteur global de Motion.
   Pose AVANT tout le reste : les connectedCallback des elements custom du
   theme s'executent pendant global.js, et certains lancent une animation
   immediatement. Arriver apres serait arriver trop tard.
   NOTE : on ne touche NI a MotionGlobalConfig.WillChange (qui ferait ecrire
   will-change par Motion, en concurrence de notre gestion), NI a
   useManualTiming (qui remplacerait l'horloge et gelerait tout).
---------------------------------------------------------------------------- */
function appliquerInterrupteurMotion() {
  MotionGlobalConfig.skipAnimations = REDUIT.matches;
}
appliquerInterrupteurMotion();
REDUIT.addEventListener('change', appliquerInterrupteurMotion);

/* ----------------------------------------------------------------------------
   COUCHE 2 — sortie anticipee. Sous « reduire les animations » on ne pose meme
   pas la classe html.rdg-motion-on : l'etat cache du CSS n'existe donc jamais,
   et il n'y a rien a reveler. C'est une desactivation, pas une attenuation.
---------------------------------------------------------------------------- */
const MOUVEMENT_ACTIF = !REDUIT.matches;

/* ============================================================================
   REGISTRE DES CIBLES
   Tout est ici, et AUCUN gabarit Liquid n'est modifie. C'est ce qui rend le
   lot rejouable sans risque de push --only mal place sur un fichier du theme.
   ============================================================================ */

const CIBLES_REVEAL = [
  /* --- Accueil -------------------------------------------------------- */
  // Cartes categories. INTERDIT : le slideshow, c'est le LCP.
  { conteneur: '.hdt-section_media-cards .hdt-slider__container', enfants: '.hdt-media-card-item' },
  { conteneur: '[id^="rdg-ess-"]', enfants: '.rdg-ess__head' },
  { conteneur: '[id^="rdg-ess-"] .rdg-ess__strip', enfants: '.rdg-ess__card' },

  /* --- Collection ------------------------------------------------------ */
  // La grille. INTERDIT : le hero et la barre de sous-collections.
  { conteneur: '[id^="products-"]', enfants: '.hdt-card-product' },
  { conteneur: '.hdt-section__collection-list .hdt-section__header', enfants: ':scope > *' },
  { conteneur: '.hdt-section__collection-list .hdt-slider__container', enfants: '.hdt-slider__slide' },

  /* --- Fiche produit --------------------------------------------------- */
  // INTERDIT : toute la section principale (galerie 86vh, colonnes sticky,
  // prix, bouton d'achat). « Avoir le look » est laisse statique a dessein :
  // il apparaitrait au milieu d'une colonne qui glisse, illisible.
  { conteneur: '[id^="rdg-avis-"]', enfants: '.rdg-avis__head' },
  { conteneur: '[id^="rdg-avis-"] .rdg-avis__track', enfants: '.rdg-avis__card' },
  { conteneur: '.hdt-slider-recommended .hdt-slider__container', enfants: '.hdt-slider__slide' },
];

// mask-line : deux titres en production, et c'est volontaire. C'est une
// primitive chere : elle ne doit pas devenir un decor par defaut.
const CIBLES_MASK = ['[id^="rdg-ess-"] .rdg-ess__title', '[id^="rdg-avis-"] .rdg-avis__title'];

const PLAFOND_STAGGER = 6;
const PAS_STAGGER_MS = 70;

/* ============================================================================
   REVEAL-UP
   ============================================================================ */

const vus = new WeakSet();

/* will-change : pose au DECLENCHEMENT, pas a l'observation.
   Le brief dit « a l'observation ». Sur une collection on observe 29 cartes
   d'un coup : ce serait 29 couches de compositing permanentes sur mobile,
   soit exactement la regression de fluidite que la regle veut eviter. Au
   declenchement, on en a environ quatre a la fois — la largeur d'une rangee. */
function poserWillChange(el) {
  el.style.willChange = 'opacity, transform';
}

function retirerWillChange(el, delaiMs) {
  let fait = false;
  const nettoyer = () => {
    if (fait) return;
    fait = true;
    el.style.willChange = '';
  };
  el.addEventListener('transitionend', (e) => {
    if (e.target === el && (e.propertyName === 'opacity' || e.propertyName === 'transform')) nettoyer();
  }, { once: false });
  // Filet indispensable : transitionend ne part PAS si l'element est masque,
  // si la transition est annulee, ou si la duree vaut 0ms — ce qui est
  // precisement le cas sous prefers-reduced-motion.
  window.setTimeout(nettoyer, delaiMs + 900);
}

function montrer(el, index) {
  if (vus.has(el)) return;
  vus.add(el);
  const delai = Math.min(index, PLAFOND_STAGGER) * PAS_STAGGER_MS;
  poserWillChange(el);
  el.style.transitionDelay = delai + 'ms';
  el.classList.add('rdg-reveal--shown');
  retirerWillChange(el, delai + 650);
}

/* Ligne de flottaison. Deux precautions :
   · on ajoute une marge de securite, parce que les images pas encore chargees
     faussent les hauteurs vers le haut au moment de l'init ;
   · un element deja visible ne doit pas seulement etre « revele tout de
     suite », il ne doit avoir AUCUNE transition — d'ou la classe instant. */
function dansLePremierEcran(el) {
  const r = el.getBoundingClientRect();
  return r.top < window.innerHeight * 1.05;
}

function enroler(el, index) {
  if (vus.has(el) || el.classList.contains('rdg-reveal')) return;
  if (dansLePremierEcran(el)) {
    vus.add(el);
    el.classList.add('rdg-reveal--instant');
    return;
  }
  el.classList.add('rdg-reveal');
  observateur.observe(el);
  el.dataset.rdgIndex = String(index);
}

const observateur = new IntersectionObserver(
  (entrees) => {
    for (const e of entrees) {
      if (!e.isIntersecting) continue;             // jamais intersectionRatio
      observateur.unobserve(e.target);             // one-shot
      montrer(e.target, Number(e.target.dataset.rdgIndex || 0));
    }
  },
  { threshold: [0, 0.15], rootMargin: '0px 0px -10% 0px' }
);

/* ----------------------------------------------------------------------------
   FILET DE SECURITE — aucun contenu ne doit rester invisible A VIE.

   IntersectionObserver peut ne jamais notifier un element : s'il est translate
   hors de la zone de clip d'un ancetre overflow:hidden (carrousels), si son
   rectangle est vide, ou si un ancetre est masque au moment de l'observation.
   Dans ce cas l'element garde opacity:0 pour toujours.

   Ce n'est pas theorique : c'est EXACTEMENT le bug qui etait en production sur
   ce theme (grille collection invisible), corrige au lot 0. On ne le
   reintroduit pas par une autre porte.

   Balayage passif au defilement, en rAF : tout element encore cache dont le
   haut est entre dans le viewport est revele, que l'observateur ait parle ou
   non. Le listener se retire de lui-meme quand il n'y a plus rien a reveler.
---------------------------------------------------------------------------- */
let balayagePrevu = false;

function balayer() {
  balayagePrevu = false;
  const restants = document.querySelectorAll('.rdg-reveal:not(.rdg-reveal--shown):not(.rdg-reveal--instant)');
  if (!restants.length) {
    window.removeEventListener('scroll', planifierBalayage);
    window.removeEventListener('resize', planifierBalayage);
    return;
  }
  for (const el of restants) {
    if (el.getBoundingClientRect().top < window.innerHeight) {
      observateur.unobserve(el);
      montrer(el, Number(el.dataset.rdgIndex || 0));
    }
  }
}

function planifierBalayage() {
  if (balayagePrevu) return;
  balayagePrevu = true;
  window.requestAnimationFrame(balayer);
}

function armerFilet() {
  window.addEventListener('scroll', planifierBalayage, { passive: true });
  window.addEventListener('resize', planifierBalayage, { passive: true });
}

/* Index de DECALAGE, calcule par RANGEE et non par element.
   Le brief §9 demande « reveal-up par rangee, stagger 70ms » : les deux
   vignettes d'une meme rangee doivent monter ENSEMBLE. Un index par element
   donnait 70ms d'ecart entre la colonne de gauche et celle de droite — l'oeil
   le lit comme un defaut d'alignement, pas comme un rythme.

   La rangee est deduite de offsetTop, que les elements d'une meme rangee
   partagent, plutot que du nombre de colonnes : celui-ci change avec la
   largeur, avec le reglage de section et avec le mode pleine largeur. La
   tolerance de 4px absorbe les vignettes de hauteur legerement inegale. */
function indexParRangee(elements) {
  const index = [];
  let rangee = -1;
  let hautDeRangee = null;
  for (const el of elements) {
    const haut = el.offsetTop;
    if (hautDeRangee === null || Math.abs(haut - hautDeRangee) > 4) {
      rangee += 1;
      hautDeRangee = haut;
    }
    index.push(rangee);
  }
  return index;
}

function armerReveals(racine = document) {
  for (const { conteneur, enfants } of CIBLES_REVEAL) {
    for (const c of racine.querySelectorAll(conteneur)) {
      const liste = [...c.querySelectorAll(enfants)];
      const index = indexParRangee(liste);
      liste.forEach((el, i) => enroler(el, index[i]));
    }
  }
}

/* ============================================================================
   MASK-LINE — avec garde de hauteur
   Decouper un titre en lignes peut changer sa hauteur d'un pixel, donc
   degrader le CLS. Or le CLS du site est a 0,000 et Lucien a demande qu'il
   soit protege a chaque lot. On mesure, on decoupe, on remesure : si l'ecart
   depasse 1px, on RESTAURE le titre et on abandonne le mask dessus.
   Un CLS parfait vaut mieux qu'un effet de titre.
   ============================================================================ */

function masquerParLignes(titre) {
  if (titre.dataset.rdgSplit) return;
  const texte = titre.textContent.replace(/\s+/g, ' ').trim();
  if (!texte) return;

  const htmlOrigine = titre.innerHTML;
  const hauteurAvant = titre.getBoundingClientRect().height;

  // On enveloppe chaque mot pour pouvoir le situer, puis on regroupe par
  // offsetTop : c'est le navigateur qui decide ou les lignes tombent, pas nous.
  titre.classList.add('rdg-mask');
  titre.innerHTML = texte
    .split(' ')
    .map((mot) => `<span data-rdg-mot>${mot}</span>`)
    .join(' ');

  const mots = [...titre.querySelectorAll('[data-rdg-mot]')];
  const lignes = [];
  let courante = null;
  for (const mot of mots) {
    const haut = mot.offsetTop;
    if (!courante || Math.abs(haut - courante.haut) > 2) {
      courante = { haut, mots: [] };
      lignes.push(courante);
    }
    courante.mots.push(mot.textContent);
  }

  titre.innerHTML = lignes
    .map((l) => `<span class="rdg-mask__line"><span class="rdg-mask__inner">${l.mots.join(' ')}</span></span>`)
    .join('');

  const hauteurApres = titre.getBoundingClientRect().height;
  if (Math.abs(hauteurApres - hauteurAvant) > 1) {
    // Garde CLS : on renonce proprement.
    titre.innerHTML = htmlOrigine;
    titre.classList.remove('rdg-mask');
    titre.dataset.rdgSplit = '0';
    return;
  }

  titre.dataset.rdgSplit = '1';
  observateurMask.observe(titre);
}

const observateurMask = new IntersectionObserver(
  (entrees) => {
    for (const e of entrees) {
      if (!e.isIntersecting) continue;
      observateurMask.unobserve(e.target);
      [...e.target.querySelectorAll('.rdg-mask__line')].forEach((ligne, i) => {
        ligne.style.transitionDelay = i * PAS_STAGGER_MS + 'ms';
        window.setTimeout(() => ligne.classList.add('rdg-mask__line--shown'), 0);
      });
    }
  },
  { threshold: [0, 0.15], rootMargin: '0px 0px -10% 0px' }
);

function armerMasks(racine = document) {
  for (const sel of CIBLES_MASK) {
    for (const titre of racine.querySelectorAll(sel)) {
      if (dansLePremierEcran(titre)) continue;   // regle non negociable n°1
      masquerParLignes(titre);
    }
  }
}

/* ============================================================================
   DEMARRAGE ET REBRANCHEMENT
   Sans rebranchement, un reveal pose au chargement disparait des qu'on filtre
   une collection : le theme remplace le DOM et les nouveaux noeuds n'ont
   jamais ete enroles.
   ============================================================================ */

function demarrer() {
  if (!MOUVEMENT_ACTIF) return;                 // couche 2 : rien n'est cache
  document.documentElement.classList.add('rdg-motion-on');
  armerReveals();
  armerFilet();
  // mask-line mesure des lignes : il FAUT attendre que les polices soient
  // peintes, sinon on decoupe sur les metriques du repli et le decoupage est
  // faux. EB Garamond arrive en woff2 avec font-display:swap.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => armerMasks());
  } else {
    armerMasks();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', demarrer, { once: true });
} else {
  demarrer();
}

/* Rendu de facettes : le theme anime DEJA la section entiere (facets.js:697).
   On ne rearme donc pas — on marque le sous-arbre comme vu pour qu'il ne
   reste pas cache, et on n'y touche plus. */
document.addEventListener('facet:render', (e) => {
  const cible = e.target instanceof Element ? e.target : document;
  cible.querySelectorAll?.('.rdg-reveal').forEach((el) => {
    vus.add(el);
    el.classList.add('rdg-reveal--instant');
  });
});

/* Mise a jour produit et chargements paginés : on enrole le nouveau contenu. */
for (const evenement of ['products:update', 'shopify:section:load']) {
  document.addEventListener(evenement, (e) => {
    if (!MOUVEMENT_ACTIF) return;
    const racine = e.target instanceof Element ? e.target : document;
    armerReveals(racine);
  });
}


/* ============================================================================
   SHEET-IN — TIROIR PANIER (lot 6, 21/08/2026)

   Le lot 4 avait reporte cette primitive en pensant qu'il faudrait ECRIRE
   l'animation. C'etait faux : la classe Drawer de global.js sait deja faire
   `translate3d(0, 100%, 0) -> 0` des que l'attribut `pos` vaut "bottom". Deux
   attributs dans sections/cart-drawer.liquid ont suffi a l'activer.

   Il reste UNE chose a corriger : la duree. Le theme ouvre en 500ms et ferme en
   400ms ; le brief fixe --dur-sheet a 320ms (sortie 260ms). Sur une surface qui
   couvre tout l'ecran, 500ms se sent.

   POURQUOI SUR cart-drawer ET NON SUR hdt-drawer
   CartDrawer etend Drawer. Poser la surcharge sur Drawer.prototype toucherait
   AUSSI le menu, la recherche et les filtres — or le menu est le lot 7 et sa
   geometrie est gelee. Posee sur CartDrawer.prototype, elle masque celle du
   parent pour les seules instances du panier.

   CONTRAINTES A NE PAS CASSER, verifiees dans global.js :
     · la methode doit rester SYNCHRONE et retourner l'objet Motion — l'ouverture
       fait `const a = this.animateDialogOpen(); if (jump) a.complete(); await a;`
       et `.complete()` est une API Motion, pas WAAPI ;
     · au-dessus de 768px `pos` vaut "right" : on delegue alors au parent, le
       tiroir lateral garde exactement le mouvement du theme ;
     · `MotionGlobalConfig.skipAnimations` (couche 3, posee plus haut) fait
       terminer instantanement toute animation Motion : « reduire les
       animations » est donc deja couvert, sans branche dediee ici.
   ========================================================================== */

customElements.whenDefined('cart-drawer').then((CartDrawerClass) => {
  const proto = CartDrawerClass.prototype;
  const parent = Object.getPrototypeOf(proto);   // Drawer.prototype
  if (!parent || typeof parent.animateDialogOpen !== 'function') return;

  const SORTIE = [0.16, 1, 0.30, 1];   // --ease-out, en tableau (format Motion)

  /* Les blocs qui montent en decale derriere la surface. On les cherche a
     chaque ouverture et jamais d'avance : la JS du panier remplace ces noeuds a
     chaque changement de quantite, une liste mise en cache pointerait sur des
     elements detaches. */
  const BLOCS = [
    '.hdt-drawer__header',
    '.rdg-fsb',
    '.hdt-cart-item',
    '.hdt-cart-complementary',
    '.hdt-cart-drawer__footer'
  ].join(', ');
  const PLAFOND = 6;      // au-dela, le dernier bloc arriverait apres la surface
  /* La vague des blocs internes, recalee sur la surface a 720ms.
     Le dernier des 6 blocs part a 0.12 + 5x0.07 = 470ms et finit a 1.01s, soit
     APRES la surface — et c'est voulu. Sur une expo-out le panneau parait pose
     bien avant la fin de sa course (~300ms) : le contenu qui acheve de se
     placer derriere lui se lit comme un depot, pas comme un retard. Les faire
     finir ensemble, au contraire, tasserait toute la vague dans les 200
     premieres millisecondes et il ne resterait aucun decalage a voir. */
  const PAS = 0.07;
  const DEPART = 0.12;

  function blocs(dialog) {
    return Array.prototype.slice.call(dialog.querySelectorAll(BLOCS), 0, PLAFOND);
  }

  /* FILET DE SECURITE — meme logique qu'au lot 4.
     Une animation d'opacite interrompue peut laisser un opacity:0 en style
     inline, donc un pan de panier invisible A VIE. C'est exactement le bug qui
     etait en production sur la grille collection. On efface donc les styles
     inline a la fin de l'ouverture ET au debut de la fermeture : deux filets
     independants, aucun des deux ne suffirait seul. */
  function nettoyer(dialog) {
    blocs(dialog).forEach((el) => {
      el.style.removeProperty('opacity');
      el.style.removeProperty('transform');
    });
  }

  /* ==========================================================================
     28/08/2026 — LE TIROIR VIENT DE LA DROITE, ET PLUS FLUIDEMENT
     ==========================================================================
     Lucien : « slider cote droit animation type webflow hyper fluide ».

     CE QUI A CHANGE DANS LE LIQUID : l'attribut est passe de
     `pos="bottom" pos-min-width="768:right"` a `pos="right"`. Le tiroir
     montait du BAS au telephone et n'arrivait par la droite qu'au-dessus de
     768px — deux gestes differents pour le meme objet. La geometrie, elle, ne
     bouge pas : le plein ecran mobile est impose par avenir-next.css en
     `!important` (inset/width/height), independamment de `pos`. L'attribut ne
     pilote QUE la direction de l'animation.

     POURQUOI L'AXE EST CALCULE ET NON ECRIT EN DUR : la version d'avant
     testait `this.pos !== 'bottom'` et rendait la main au theme sinon. Avec
     l'attribut passe a "right", cette garde aurait desactive tout le bloc en
     SILENCE — le decalage des blocs internes aurait disparu sans que rien ne
     le signale. On derive donc le deplacement de `pos`, et les deux valeurs
     restent servies : si quelqu'un remet "bottom" un jour, l'animation suit.

     LES DUREES ONT ETE ALLONGEES, ET CELA REVIENT SUR LE BRIEF, qui fixait
     --dur-sheet a 320ms et interdisait d'allonger la sortie. La demande du
     28/08 est explicite et posterieure : d'abord « animation type webflow
     hyper fluide » (520/380), puis « ralenti l'animation d'apparition en
     restant fluide et naturelle, pro webflow. Celle partante aussi ».
     On est donc a 720ms a l'entree et 520ms a la sortie.

     LA COURBE COMPTE PLUS QUE LA DUREE pour l'impression de fluidite :
     [0.16, 1, 0.30, 1] est une expo-out, elle decelere longuement. C'est elle
     qui donne le glisse « webflow », et c'est aussi elle qui rend ces durees
     supportables : sur une expo-out, le panneau a parcouru l'essentiel de sa
     course a ~40% du temps. A 720ms il parait pose vers 300ms, le reste n'est
     qu'un amortissement que l'oeil lit comme du velours et non comme de
     l'attente.

     LA SORTIE RESTE PLUS COURTE QUE L'ENTREE (520 < 720) : la cliente a deja
     decide de partir, la faire attendre pour la regarder partir est le seul
     endroit ou une animation devient franchement penible.

     LE COMPROMIS, DIT UNE FOIS : ce tiroir s'ouvre a CHAQUE ajout au panier,
     pas seulement quand on le demande. C'est le seul argument contre ces
     durees, et Lucien l'a tranche en connaissance de cause. Si la conversion
     mobile bougeait, les deux nombres ci-dessous sont le premier bouton a
     tourner.

     Sous « reduire les animations », rien de tout ceci ne tourne :
     `MotionGlobalConfig.skipAnimations` est pose en tete de fichier et coupe
     Motion a la racine. */

  const AXES = {
    bottom: { hors: 'translate3d(0, 100%, 0)', bloc: 'translateY(10px)' },
    right:  { hors: 'translate3d(100%, 0, 0)', bloc: 'translateX(16px)' }
  };

  proto.animateDialogOpen = function () {
    const axe = AXES[this.pos];
    if (!axe) return parent.animateDialogOpen.call(this);

    const dialog = this.dialog;
    const sequence = [
      [dialog, { transform: [axe.hors, 'translate3d(0, 0, 0)'] },
        { duration: 0.72, ease: SORTIE }]
    ];
    /* Les blocs internes glissent DANS LE SENS DE LA SURFACE (translateX quand
       elle vient de la droite) : un contenu qui monte pendant que le panneau
       arrive de cote donne deux mouvements contradictoires dans le meme cadre. */
    blocs(dialog).forEach((el, i) => {
      sequence.push([
        el,
        { opacity: [0, 1], transform: [axe.bloc, 'translate3d(0, 0, 0)'] },
        { duration: 0.54, ease: SORTIE, at: DEPART + i * PAS }
      ]);
    });

    const animation = animate(sequence);
    animation.then(() => nettoyer(dialog), () => nettoyer(dialog));
    return animation;
  };

  proto.animateDialogClose = function () {
    const axe = AXES[this.pos];
    if (!axe) return parent.animateDialogClose.call(this);
    /* Second filet : quoi qu'il soit arrive a l'ouverture, on repart d'une
       ardoise propre. Sinon un opacity:0 fige survivrait a la fermeture et le
       panier rouvrirait ampute. */
    nettoyer(this.dialog);
    return animate(
      this.dialog,
      { transform: ['translate3d(0, 0, 0)', axe.hors] },
      { duration: 0.52, ease: SORTIE }
    );
  };
});


/* ============================================================================
   MENU — FERMETURE AU GLISSEMENT VERS LE BAS (lot 7, 21/08/2026)

   Le brief §5.1 demande « la fermeture au swipe vers le bas et a Escape ».
   Escape est natif sur <dialog> (evenement `cancel`) : rien a ecrire, mais
   c'est verifie au navigateur, pas suppose.

   Le geste, lui, n'existe pas dans le theme.

   TROIS PRECAUTIONS, chacune contre un faux positif reel :
     1. on n'arme le geste QUE si la zone defilante est en haut. Sinon un
        defilement de la navigation vers le bas fermerait le menu ;
     2. on exige que le geste soit franchement vertical (|dy| > |dx|), sinon
        le retour arriere du drilldown, qui est horizontal, declencherait ;
     3. on ne bouge la surface qu'apres 8px, pour ne pas la faire tressaillir
        au moindre appui.

   Le suivi du doigt passe par une transform sur le WRAPPER et non sur le
   <dialog> : le dialogue porte le clip-path de l'animation d'ouverture, une
   transform concurrente sur le meme element se battrait avec elle.
   ========================================================================== */

(function () {
  const SEUIL = 90;         // px au-dela desquels on ferme
  const AMORCE = 8;         // px avant de commencer a suivre le doigt
  const RESISTANCE = 0.55;  // la surface suit moins vite que le doigt

  function armer() {
    const dialog = document.getElementById('MenuDrawer');
    if (!dialog || dialog.__rdgSwipe) return;
    dialog.__rdgSwipe = true;

    const wrapper = dialog.querySelector('.hdt-drawer_wrapper');
    if (!wrapper) return;

    let y0 = 0, x0 = 0, dy = 0, actif = false, suit = false;

    const zoneDefilante = () => dialog.querySelector('.hdt-drawer__inner');

    const reinitialiser = (anime) => {
      suit = false; actif = false; dy = 0;
      wrapper.style.transition = anime ? 'transform 220ms cubic-bezier(0.16,1,0.30,1)' : '';
      wrapper.style.transform = '';
      if (anime) {
        setTimeout(() => { wrapper.style.transition = ''; }, 260);
      }
    };

    dialog.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      const zone = zoneDefilante();
      if (zone && zone.scrollTop > 0) return;   // precaution 1
      y0 = e.touches[0].clientY;
      x0 = e.touches[0].clientX;
      dy = 0;
      actif = true;
      wrapper.style.transition = '';
    }, { passive: true });

    dialog.addEventListener('touchmove', (e) => {
      if (!actif || e.touches.length !== 1) return;
      const t = e.touches[0];
      const ecartY = t.clientY - y0;
      const ecartX = t.clientX - x0;
      if (ecartY <= 0 || Math.abs(ecartY) <= Math.abs(ecartX)) {   // precaution 2
        if (!suit) actif = false;
        return;
      }
      if (!suit && ecartY < AMORCE) return;                        // precaution 3
      suit = true;
      dy = ecartY;
      wrapper.style.transform = 'translateY(' + (dy * RESISTANCE).toFixed(1) + 'px)';
    }, { passive: true });

    const fin = () => {
      if (!actif) { reinitialiser(false); return; }
      if (suit && dy > SEUIL) {
        /* On efface la transform AVANT de fermer : l'animation de fermeture du
           theme agit sur le clip-path du wrapper, une transform residuelle la
           ferait partir de travers. */
        reinitialiser(false);
        const hote = dialog.closest('hdt-drawer');
        if (hote && typeof hote.close === 'function') hote.close();
        else dialog.close();
        return;
      }
      reinitialiser(suit);
    };

    dialog.addEventListener('touchend', fin, { passive: true });
    dialog.addEventListener('touchcancel', fin, { passive: true });
  }

  if (document.readyState !== 'loading') armer();
  else document.addEventListener('DOMContentLoaded', armer);
  document.addEventListener('shopify:section:load', armer);
})();


/* ============================================================================
   MENU — SEQUENCE D'OUVERTURE ORCHESTREE (lot 7, 21/08/2026)

   Le brief §5.2 fixe la sequence :
     t=0     voile : clipPath inset(100% 0 0 0) -> inset(0 0 0 0), 480ms
     t=150ms entrees de nav : mask-line, stagger 60ms
     t=280ms bloc image + pied : reveal-up, stagger 70ms
     fermeture : 380ms, sans stagger, tout part ensemble

   CE QUE LE THEME FAISAIT DEJA, ET CE QUI MANQUAIT
   Le clipPath existe : c'est le chemin d'animation choisi par la classe
   Drawer quand hdt-drawer--indent est presente (d'ou le maintien de cette
   classe, voir avenir-next.css). Manquaient la DUREE (350ms au lieu de 480)
   et surtout le decale : le theme se contentait d'un fondu global des trois
   blocs en 150ms.

   PORTEE. La surcharge est posee sur Drawer.prototype — partagee avec la
   recherche, les filtres et le panier — donc elle commence par verifier
   l'identifiant du dialogue et delegue a l'original dans tous les autres cas.
   La surcharge du panier, posee plus haut sur CartDrawer.prototype, resout
   `parent.animateDialogOpen` A L'APPEL : elle traversera cette fonction, qui
   la renverra aussitot vers l'originale. Les deux ordres de chargement
   fonctionnent.

   MASK-LINE. Chaque entree de navigation tient sur une ligne : on masque au
   niveau du <li> (overflow:hidden) et on fait monter le lien de 100%. Pas
   besoin du decoupage par ligne utilise pour les titres editoriaux.
   ========================================================================== */

customElements.whenDefined('hdt-drawer').then((DrawerClass) => {
  const proto = DrawerClass.prototype;
  const ouvrirOriginal = proto.animateDialogOpen;
  const fermerOriginal = proto.animateDialogClose;
  if (typeof ouvrirOriginal !== 'function') return;

  const COURBE = [0.16, 1, 0.30, 1];

  const estLeMenu = (el) => el && el.dialog && el.dialog.id === 'MenuDrawer';

  function entrees(dialog) {
    return Array.prototype.slice.call(
      dialog.querySelectorAll('.hdt-menu-drawer-list > li'), 0, 8
    );
  }
  function basDeMenu(dialog) {
    return Array.prototype.slice.call(
      dialog.querySelectorAll('.rdg-menu-promo, .hdt-drawer__footer > *'), 0, 4
    );
  }

  /* FILET DE SECURITE, meme logique qu'au lot 4 et au lot 6 : une animation
     d'opacite ou de translation interrompue laisserait un menu ampute. On
     nettoie a la fin de l'ouverture ET au debut de la fermeture. */
  /* La CIBLE ANIMABLE d'une entree de menu.

     Corrige le 21/08 : la section « Vetements » n'apparaissait pas alors que
     les autres entrees si. Cause exacte — une entree SANS sous-menu s'ecrit
     <li><a>, mais une entree AVEC sous-menu s'ecrit
     <li><hdt-accordion class="hdt-contents"><details>. Or un element en
     `display: contents` NE GENERE AUCUNE BOITE : ni transform ni opacity ne
     s'y appliquent. On animait donc dans le vide, sans la moindre erreur.

     On traverse les enveloppes en display:contents jusqu'au premier element
     qui produit vraiment une boite. Ecrit en boucle et non en un cran, parce
     que rien n'interdit au theme d'en empiler deux. */
  function cibleAnimable(li) {
    let el = li.firstElementChild;
    let garde = 0;
    while (el && getComputedStyle(el).display === 'contents' && garde++ < 4) {
      el = el.firstElementChild;
    }
    return el;
  }

  function nettoyer(dialog) {
    entrees(dialog).forEach((li) => {
      li.style.removeProperty('overflow');
      const cible = cibleAnimable(li);
      if (cible) {
        cible.style.removeProperty('transform');
        cible.style.removeProperty('opacity');
      }
    });
    basDeMenu(dialog).forEach((el) => {
      el.style.removeProperty('opacity');
      el.style.removeProperty('transform');
    });
  }

  proto.animateDialogOpen = function () {
    if (!estLeMenu(this)) return ouvrirOriginal.call(this);

    const dialog = this.dialog;
    const panneau = dialog.firstElementChild;
    const sequence = [
      [panneau,
        { clipPath: ['inset(100% 0% 0% 0%)', 'inset(0% 0% 0% 0%)'] },
        { duration: 0.48, ease: COURBE }]
    ];

    entrees(dialog).forEach((li, i) => {
      const cible = cibleAnimable(li);
      if (!cible) return;
      li.style.setProperty('overflow', 'hidden');
      sequence.push([
        cible,
        { transform: ['translateY(100%)', 'translateY(0%)'], opacity: [0, 1] },
        { duration: 0.5, ease: COURBE, at: 0.15 + i * 0.06 }
      ]);
    });

    basDeMenu(dialog).forEach((el, i) => {
      sequence.push([
        el,
        { opacity: [0, 1], transform: ['translateY(14px)', 'translateY(0px)'] },
        { duration: 0.45, ease: COURBE, at: 0.28 + i * 0.07 }
      ]);
    });

    const animation = animate(sequence);
    animation.then(() => nettoyer(dialog), () => nettoyer(dialog));
    return animation;
  };

  proto.animateDialogClose = function () {
    if (!estLeMenu(this)) return fermerOriginal.call(this);
    /* « Sans stagger, tout part ensemble » : la fermeture ne raconte rien, elle
       libere. Et on repart d'une ardoise propre avant de fermer. */
    nettoyer(this.dialog);
    return animate(
      this.dialog.firstElementChild,
      { clipPath: ['inset(0% 0% 0% 0%)', 'inset(100% 0% 0% 0%)'] },
      { duration: 0.38, ease: [0.65, 0, 0.35, 1] }
    );
  };
});
